import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import {
  OneOnOneGetOrCreateMeetingFromTemplateDocument,
  OneOnOnesScheduledTemplateMeetingsDocument,
  OneOnOnesTemplateDataDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import { MeetingErrorLayout } from 'modules/meetings/components/Layout/MeetingErrorLayout'
import { PageSkeleton } from 'modules/meetings/components/Skeletons/PageSkeleton'
import { useOneOnOnesContext } from 'modules/meetings/lib/hooks/useOneOnOnesContext'

const OneOnOnesTemplateRedirectPage = () => {
  const { templateId } = useParams<{ templateId: string }>()
  const { employee: currentEmployee } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const { canSeeAdvancedMeetings } = useOneOnOnesContext()
  const isPrejoin =
    pathname.includes(navigation.engagement.one_on_ones.templateId.prejoin.path) &&
    canSeeAdvancedMeetings
  const [startDate, setStartDate] = useState<string | undefined | null>(null)
  const [endDate, setEndDate] = useState<string | undefined | null>(null)

  const { data: templateData, error: templateError } = useQuery(OneOnOnesTemplateDataDocument, {
    variables: {
      templateId: String(templateId),
    },
    skip: !templateId,
  })

  const template = templateData?.meetings?.templatesConnection?.nodes?.[0]

  const {
    data: scheduledMeetings,
    loading: scheduledMeetingsLoading,
    error: scheduledMeetingsError,
  } = useQuery(OneOnOnesScheduledTemplateMeetingsDocument, {
    variables: {
      templateId: Number(template?.id),
      startDate,
      endDate,
    },
    skip: !template?.id || startDate === null || endDate === null,
  })

  const scheduledMeetingsList =
    scheduledMeetings?.meetings?.scheduledMeetingsConnection?.nodes ?? undefined

  const selectedMeeting = scheduledMeetingsList?.[0]

  const [getOrCreateMeeting, { error: createMeetingError }] = useMutation(
    OneOnOneGetOrCreateMeetingFromTemplateDocument
  )

  useEffect(() => {
    if (template?.id) {
      switch (template.frequency) {
        case 'weekly':
          setStartDate(moment().startOf('week').format('YYYY-MM-DD'))
          setEndDate(moment().endOf('week').add(10, 'week').format('YYYY-MM-DD'))
          break
        case 'monthly':
          setStartDate(moment().startOf('month').format('YYYY-MM-DD'))
          setEndDate(moment().endOf('month').add(10, 'month').format('YYYY-MM-DD'))
          break
        case 'yearly':
          setStartDate(moment().startOf('year').format('YYYY-MM-DD'))
          setEndDate(moment().endOf('year').add(10, 'year').format('YYYY-MM-DD'))
          break
        default:
          setStartDate(moment('1970-01-01').format('YYYY-MM-DD'))
          setEndDate(moment('2100-01-01').format('YYYY-MM-DD'))
      }
    }
  }, [templateData, template])

  useEffect(() => {
    const handleNavigation = async () => {
      const otherAttendee = template?.templateAttendees?.filter(
        (attendee) =>
          attendee.employee?.id && Number(attendee.employee.id) !== Number(currentEmployee?.id)
      )?.[0]

      const employeeId = otherAttendee?.employee?.id ? Number(otherAttendee.employee.id) : null

      let meetingId = selectedMeeting?.id

      // If we need to create a meeting, wait for it to complete
      if (!meetingId && selectedMeeting?.startTime && templateId) {
        const result = await getOrCreateMeeting({
          variables: {
            templateId: Number(templateId),
            timestamp: moment(selectedMeeting.startTime).toISOString(),
          },
        })
        const meeting =
          result.data?.meetingsMutations?.getOrCreateFromTemplateMeetingsMeeting?.meeting
        if (meeting?.id) meetingId = meeting.id
      }

      if (!employeeId || isNaN(employeeId) || !meetingId) {
        navigate(navigation.engagement.one_on_ones.fullPath())
        return
      }

      if (isPrejoin) {
        navigate(
          navigation.engagement.one_on_ones.employeeId.meetingId.prejoin.fullPath({
            meetingId,
            employeeId,
          })
        )
        return
      }

      navigate(
        navigation.engagement.one_on_ones.employeeId.meetingId.fullPath({
          meetingId,
          employeeId,
        })
      )
    }

    if (!scheduledMeetingsLoading && scheduledMeetings && startDate !== null && endDate !== null) {
      handleNavigation()
    }
  }, [
    currentEmployee,
    endDate,
    getOrCreateMeeting,
    isPrejoin,
    navigate,
    navigation,
    scheduledMeetings,
    scheduledMeetingsLoading,
    selectedMeeting,
    startDate,
    template,
    templateId,
  ])

  const hasError = templateError || createMeetingError || scheduledMeetingsError
  const hasNoTemplate = templateData?.meetings?.templatesConnection?.nodes?.length === 0
  const hasNoMeetings =
    scheduledMeetings?.meetings?.scheduledMeetingsConnection?.nodes?.length === 0

  if (hasError || hasNoTemplate || hasNoMeetings) {
    return <MeetingErrorLayout />
  }

  return <PageSkeleton />
}

export default OneOnOnesTemplateRedirectPage
